// lib/audioUtils.js
// Singleton Web Audio context shared across the whole app.
//
// Browser autoplay policy: audio is blocked until a user gesture occurs.
// We resume the context on any interaction and, if a sound is requested
// before it's running, we queue it as a "pending" sound that plays the
// moment the user next touches the page.  This way timer-fired popups
// always eventually make a sound without requiring an immediate gesture.

let _ctx     = null;
let _pending = null; // fn queued while context is suspended

function getCtx() {
  if (_ctx) return _ctx;
  try { _ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch { _ctx = null; }
  return _ctx;
}

function flushPending() {
  if (!_pending) return;
  const fn = _pending;
  _pending = null;
  fn();
}

export function unlockAudio() {
  const c = getCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume().then(flushPending).catch(() => {});
  else flushPending();
}

// Attach unlock to deliberate user gestures only.
// scroll is intentionally excluded: browsers fire scroll during page-restore
// on hard refresh (not a real gesture), which would trigger a premature
// AudioContext.resume() and log "AudioContext was not allowed to start".
if (typeof window !== "undefined") {
  const u = () => unlockAudio();
  window.addEventListener("click",      u, { passive: true });
  window.addEventListener("keydown",    u, { passive: true });
  window.addEventListener("touchstart", u, { passive: true });
}

function running() { const c = getCtx(); return c && c.state === "running"; }

function playOrQueue(fn) {
  // Queue silently when context isn't running yet  -  do NOT call unlockAudio()
  // here, as popup timers are not user gestures and the attempt to resume a
  // suspended context would produce a browser console warning.
  if (running()) fn();
  else _pending = fn;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function sine(c, freq, startT, vol, attack, decay) {
  const osc  = c.createOscillator();
  const gain = c.createGain();
  osc.connect(gain); gain.connect(c.destination);
  osc.type = "sine";
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, startT);
  gain.gain.linearRampToValueAtTime(vol, startT + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, startT + decay);
  osc.start(startT); osc.stop(startT + decay + 0.05);
}

// ── Exported sounds ────────────────────────────────────────────────────────

/** Soft two-note rising chime  -  Welcome popup */
export function playChime() {
  playOrQueue(() => {
    const c = getCtx(); if (!c) return;
    [[523.25, 0], [659.25, 0.14]].forEach(([f, t]) =>
      sine(c, f, c.currentTime + t, 0.05, 0.03, 1.4)
    );
  });
}

/** Warm soft descending pop  -  Support/coffee popup */
export function playPop() {
  playOrQueue(() => {
    const c = getCtx(); if (!c) return;
    const now = c.currentTime;
    [[392, 294, 0], [294, 220, 0.09]].forEach(([f, ft, offset]) => {
      const osc = c.createOscillator(); const gain = c.createGain();
      osc.connect(gain); gain.connect(c.destination); osc.type = "sine";
      const t = now + offset;
      osc.frequency.setValueAtTime(f, t); osc.frequency.exponentialRampToValueAtTime(ft, t + 0.18);
      gain.gain.setValueAtTime(0.045, t); gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
      osc.start(t); osc.stop(t + 0.5);
    });
  });
}

/** Three-note ascending fanfare  -  form success / message sent */
export function playSuccess() {
  playOrQueue(() => {
    const c = getCtx(); if (!c) return;
    [[523.25, 0], [659.25, 0.12], [783.99, 0.24]].forEach(([f, t]) =>
      sine(c, f, c.currentTime + t, 0.055, 0.025, 1.1)
    );
  });
}

/** Soft descending minor-third buzz  -  validation / form error */
export function playError() {
  playOrQueue(() => {
    const c = getCtx(); if (!c) return;
    const now = c.currentTime;
    [[349.23, 293.66, 0], [293.66, 246.94, 0.12]].forEach(([f, ft, offset]) => {
      const osc = c.createOscillator(); const gain = c.createGain();
      osc.connect(gain); gain.connect(c.destination); osc.type = "sine";
      const t = now + offset;
      osc.frequency.setValueAtTime(f, t); osc.frequency.exponentialRampToValueAtTime(ft, t + 0.14);
      gain.gain.setValueAtTime(0.04, t); gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.start(t); osc.stop(t + 0.4);
    });
  });
}

/** Bright upward heart-pop  -  liking a post */
export function playLike() {
  playOrQueue(() => {
    const c = getCtx(); if (!c) return;
    const now = c.currentTime;
    // Quick upward sweep then a soft ding
    const osc1 = c.createOscillator(); const g1 = c.createGain();
    osc1.connect(g1); g1.connect(c.destination); osc1.type = "sine";
    osc1.frequency.setValueAtTime(440, now); osc1.frequency.exponentialRampToValueAtTime(880, now + 0.1);
    g1.gain.setValueAtTime(0.05, now); g1.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    osc1.start(now); osc1.stop(now + 0.2);
    // Soft ding after
    sine(c, 1046.5, now + 0.1, 0.04, 0.02, 0.8); // C6
  });
}

/** Tiny soft click  -  theme toggle */
export function playThemeToggle() {
  playOrQueue(() => {
    const c = getCtx(); if (!c) return;
    sine(c, 600, c.currentTime, 0.03, 0.005, 0.08);
  });
}
